import { MODULES, type ModuleKey, type Permission, type Role } from "@kipo/shared";

type Who = { role: Role; permissions: readonly string[] };

/** Pagine fuori dalla navigazione che chiedono più della voce che le contiene. */
const EXTRA: { to: string; permission: Permission }[] = [
  { to: "/projects/timesheet/approvals", permission: "projects:manage" },
  { to: "/sales/quotes/new", permission: "quotes:write" },
  { to: "/sales/invoices/new", permission: "invoices:write" },
  { to: "/contracts/new", permission: "contracts:write" },
];

/** I clienti seguono solo i progetti: niente task personali né timesheet. */
const CLIENT_HIDDEN = ["/projects/my-tasks", "/projects/timesheet"];

const under = (path: string, to: string) => path === to || path.startsWith(`${to}/`);

/**
 * Pagina di un modulo aperta solo se il ruolo ha il permesso della voce di menù
 * che la contiene (la più specifica), oltre a quello d'ingresso del modulo.
 */
export function canOpen(path: string, who: Who, module: ModuleKey): boolean {
  const m = MODULES[module];
  if (!who.permissions.includes(m.entryPermission)) return false;
  if (who.role === "client" && CLIENT_HIDDEN.some((to) => under(path, to))) return false;
  // la panoramica del modulo mostra solo ciò che il ruolo può vedere: basta poter entrare nel modulo
  if (path === `/${module}/overview`) return true;
  const extra = EXTRA.find((e) => under(path, e.to));
  if (extra && !who.permissions.includes(extra.permission)) return false;
  const item = m.navigation.filter((n) => under(path, n.to)).sort((a, b) => b.to.length - a.to.length)[0];
  return !item?.permission || who.permissions.includes(item.permission);
}

/** Prima pagina del modulo che il ruolo può aprire (dove porta il link del modulo). */
export function firstOpenPath(module: ModuleKey, who: Who): string | null {
  return MODULES[module].navigation.find((n) => canOpen(n.to, who, module))?.to ?? null;
}

/**
 * Pagina principale del modulo, quella aperta dal titolo nel menu: la panoramica.
 * Vendite ha già una panoramica finanziaria, riservata a chi vede la finanza; gli altri ruoli hanno quella generica.
 */
export function moduleHome(module: ModuleKey, who: Who): string {
  return module === "sales" && who.permissions.includes("finance:read") ? "/sales" : `/${module}/overview`;
}
